import type { BlockInfoIcons, InfoIcon } from 'types/block-info-icons';
import React from 'react';
import { Wrap, Button, Paragraph } from '@leafwell/components';
import Figure from './figure';
import WysiwygContent from './wysiwyg-content';
import HeadingBody from './heading-body';

const InfoItem: React.FC<InfoIcon> = ({ title, description, icon }) => {
  return (
    <li className="grid grid-cols-[auto_1fr] gap-x-6">
      {icon && <Figure {...icon} className="w-8" />}
      <div>
        <HeadingBody
          level="5"
          tagName="p"
          title={title}
          className="font-medium !mb-2"
        />
        {description?.length > 0 && <WysiwygContent content={description} />}
      </div>
    </li>
  );
};

const InfoIcons: React.FC<BlockInfoIcons> = ({
  sectionId,
  backgroundColor,
  title,
  description,
  extraInfo,
  button,
  items,
}) => {
  const hasExtraInfo = extraInfo?.length > 0;

  return (
    <section
      id={sectionId}
      className={[
        backgroundColor === 'gray'
          ? 'bg-gray-200 my-10 md:my-12'
          : backgroundColor === 'lilac'
          ? 'bg-lilac-50 my-10 md:my-12'
          : 'bg-white',
      ].join(' ')}
    >
      <Wrap className="xl:grid-cols-2-auto xl:gap-x-24 xl:py-24">
        <div className="flex flex-col xl:justify-between xl:max-w-100">
          <span className="flex flex-col gap-y-1 xl:gap-y-3">
            <HeadingBody
              level="2"
              tagName="h2"
              title={title}
              className="font-semibold"
            />
            {description?.length > 0 && (
              <WysiwygContent content={description} />
            )}
          </span>
          <span
            className={[
              'hidden xl:flex xl:flex-col xl:gap-y-8',
              hasExtraInfo ? 'xl:mt-16' : '',
            ].join(' ')}
          >
            {hasExtraInfo && (
              <Paragraph size="small" className="leading-loose">
                {extraInfo}
              </Paragraph>
            )}
            {button && (
              <Button
                href={button.url}
                label={button.title}
                rounded={true}
                target={button.target}
                variant="secondary"
                className="w-fit h-fit"
              />
            )}
          </span>
        </div>
        {items?.length > 0 && (
          <ul className="grid xl:grid-rows-auto-auto xl:grid-flow-col gap-10 xl:gap-16">
            {items?.map(item => (
              <InfoItem
                key={item.title}
                icon={item.icon}
                title={item.title}
                description={item.description}
              />
            ))}
          </ul>
        )}
        <span className="grid gap-y-10 xl:hidden">
          {hasExtraInfo && <Paragraph size="small">{extraInfo}</Paragraph>}
          {button && (
            <Button
              href={button.url}
              label={button.title}
              rounded={true}
              target={button.target}
              variant="secondary"
              className="w-fit h-fit"
            />
          )}
        </span>
      </Wrap>
    </section>
  );
};

export default InfoIcons;
